import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import type { ClientSnapshot } from '@crew/shared';
import type { Crew } from '../net.js';

export function SidePanel({ crew, snapshot }: { crew: Crew; snapshot: ClientSnapshot }) {
  const phase = snapshot.game?.phase;
  const missionOver = phase === 'won' || phase === 'lost';
  // Hiding the log is a difficulty rule during play; the debrief is always allowed.
  const logHidden = snapshot.room.houseRules.hideFlightLog && !missionOver;
  const [tab, setTab] = useState<'log' | 'chat'>(logHidden ? 'chat' : 'log');
  const [draft, setDraft] = useState('');
  const [pinned, setPinned] = useState(false);
  const feedRef = useRef<HTMLDivElement | null>(null);
  const dockRef = useRef<HTMLElement | null>(null);
  const log = snapshot.game?.log ?? [];

  // Only what this room is allowed to read counts as something waiting to be read.
  const readable = (logHidden ? 0 : log.length) + snapshot.chat.length;
  const [seen, setSeen] = useState(readable);
  const unread = Math.max(0, readable - seen);
  const markSeen = useCallback(() => setSeen(readable), [readable]);

  // The house rule can flip between missions, so never leave a dead tab selected.
  useEffect(() => {
    if (logHidden && tab === 'log') setTab('chat');
  }, [logHidden, tab]);

  // Surface the debrief the moment a mission ends for a room that hid the log.
  useEffect(() => {
    if (missionOver && snapshot.room.houseRules.hideFlightLog) setTab('log');
  }, [missionOver, snapshot.room.houseRules.hideFlightLog]);

  useEffect(() => {
    const el = feedRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [tab, log.length, snapshot.chat.length]);

  // Pinning is the only state that outlives a pointer, so it is the only state to undo.
  useEffect(() => {
    if (!pinned) return;
    setSeen(readable);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPinned(false);
    };
    const onDown = (e: PointerEvent) => {
      if (!dockRef.current?.contains(e.target as Node)) setPinned(false);
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onDown, true);
    };
  }, [pinned, readable]);

  const send = (event: FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    void crew.sendChat(text);
  };

  const showingLog = tab === 'log' && !logHidden;

  return (
    <aside
      className={`comms${pinned ? ' is-pinned' : ''}`}
      ref={dockRef}
      onPointerEnter={markSeen}
      onFocus={markSeen}
    >
      <button
        type="button"
        className="comms__handle"
        aria-expanded={pinned}
        aria-controls="comms-panel"
        title={pinned ? 'Close comms' : 'Open flight log and chat'}
        onClick={() => setPinned((p) => !p)}
      >
        <span className="comms__handle-icon" aria-hidden="true" />
        <span className="comms__handle-label">Comms</span>
        {unread > 0 ? (
          <span key={unread} className="comms__badge" aria-label={`${unread} unread`}>
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>

      <div className="side" id="comms-panel">
        <div className="side__tabs" role="tablist">
          {logHidden ? null : (
            <button
              role="tab"
              aria-selected={showingLog}
              className={showingLog ? 'is-active' : ''}
              onClick={() => setTab('log')}
            >
              {missionOver && snapshot.room.houseRules.hideFlightLog ? 'Debrief' : 'Flight log'}
            </button>
          )}
          <button
            role="tab"
            aria-selected={tab === 'chat' || logHidden}
            className={tab === 'chat' || logHidden ? 'is-active' : ''}
            onClick={() => setTab('chat')}
          >
            Chat
          </button>
        </div>

        <div className="side__feed" ref={feedRef}>
          {showingLog ? (
            log.length === 0 ? (
              <p className="hint">Nothing logged yet.</p>
            ) : (
              log.map((entry) => (
                <p key={entry.id} className={`log log--${entry.kind}`}>
                  {entry.text}
                </p>
              ))
            )
          ) : (
            <>
              {logHidden ? (
                <p className="side__blackout">
                  Flight log is off for this room. Nobody can look back at what was played.
                </p>
              ) : null}
              {snapshot.chat.length === 0 ? (
                <p className="hint">No messages yet.</p>
              ) : (
                snapshot.chat.map((m) => (
                  <p key={m.id} className={`chat ${m.playerId ? '' : 'chat--system'}`}>
                    <strong className={`colour-${snapshot.room.players.find((p) => p.id === m.playerId)?.avatarColor ?? 'gray'}`}>
                      {m.name}
                    </strong> {m.text}
                  </p>
                ))
              )}
            </>
          )}
        </div>

        <form className="side__compose" onSubmit={send}>
          <input
            value={draft}
            maxLength={240}
            placeholder="Say something"
            onChange={(e) => setDraft(e.target.value)}
            aria-label="Chat message"
          />
          <button className="btn btn--small" type="submit" disabled={!draft.trim()}>
            Send
          </button>
        </form>
      </div>
    </aside>
  );
}
